'use client';

import { useMemo, useState } from 'react';
// Deep import rather than the barrel — see header-analyzer-tool.tsx.
import { generateEmailSignatureHtml } from '@aegis/shared/src/utils/email-record-generators';
import { Check, Copy, Lock } from 'lucide-react';

/** Builds a signature entirely in the browser — nothing is sent to us. */
export function SignatureGeneratorTool() {
  const [fields, setFields] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    website: '',
  });
  const [copied, setCopied] = useState<'html' | 'text' | null>(null);

  const result = useMemo(
    () => generateEmailSignatureHtml({ ...fields, name: fields.name || 'Your Name' }),
    [fields],
  );

  async function copy(kind: 'html' | 'text') {
    await navigator.clipboard.writeText(kind === 'html' ? result.html : result.text);
    setCopied(kind);
    setTimeout(() => setCopied(null), 2000);
  }

  const set = (key: keyof typeof fields) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFields((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div>
      <p className="mb-4 flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-2 text-xs font-medium text-primary">
        <Lock className="h-3.5 w-3.5" />
        Generated in your browser. Nothing is sent to us.
      </p>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          {(
            [
              ['name', 'Full name'],
              ['title', 'Job title'],
              ['company', 'Company'],
              ['email', 'Email'],
              ['phone', 'Phone'],
              ['website', 'Website'],
            ] as const
          ).map(([key, label]) => (
            <div key={key}>
              <label htmlFor={key} className="text-sm font-medium">{label}</label>
              <input
                id={key}
                value={fields[key]}
                onChange={set(key)}
                className="mt-1.5 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>
          ))}
        </div>

        <div>
          <h2 className="text-sm font-semibold">Preview</h2>
          <div
            className="mt-2 rounded-xl border border-border bg-card p-5"
            // The HTML comes from generateEmailSignatureHtml, which escapes
            // every user-supplied value and rejects non-http(s)/mailto URLs.
            dangerouslySetInnerHTML={{ __html: result.html }}
          />

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => copy('html')}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              {copied === 'html' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              Copy HTML
            </button>
            <button
              type="button"
              onClick={() => copy('text')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium"
            >
              {copied === 'text' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              Copy plain text
            </button>
          </div>

          <ul className="mt-4 space-y-1.5">
            {result.warnings.map((warning) => (
              <li key={warning} className="text-xs text-muted-foreground">{warning}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
